/**
 * KundenfreigabeSection — the Ausschreibungs-Screen Tab 4c body (Kundenfreigabe & Entscheid, P2
 * Placement Slice 12). Hosts the Zustellen / Kanal / Entscheid dialogs; renders the Zustellung card
 * (channel, log timeline, docx download) + the Kundenentscheid cards (with the promotion line).
 */

import { useEffect, useState } from 'react';
import {
  KUNDENENTSCHEID_ABWEICHEND_LABEL,
  KUNDENFREIGABE_NICHT_FREIGEGEBEN_HINWEIS,
  KUNDENFREIGABE_VERALTET_HINWEIS,
  type KundenentscheidErfassenBody,
  type KundenfreigabeKanalBody,
  type KundenfreigabeZustellenBody,
} from '@brokly/shared';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { dateTimeShortDe, dateShortDe } from '../../lib/format';
import { KundenfreigabeStatusBadge } from './KundenfreigabeStatusBadge';
import { KundenfreigabeZustellenDialog } from './KundenfreigabeZustellenDialog';
import { KundenfreigabeKanalDialog } from './KundenfreigabeKanalDialog';
import { KundenentscheidDialog } from './KundenentscheidDialog';
import {
  usePlacementKundenfreigabe,
  useZustellenKundenfreigabe,
  useKanalKundenfreigabe,
  useErneutSendenKundenfreigabe,
  useVersandAufloesenKundenfreigabe,
  useEntscheidKundenfreigabe,
} from './usePlacementKundenfreigabe';

type OpenKind = 'zustellen' | 'entscheid' | null;

export function KundenfreigabeSection({
  placementId,
  canWrite,
  openKind = null,
  onOpenHandled,
}: {
  placementId: string;
  canWrite: boolean;
  openKind?: OpenKind;
  onOpenHandled?: () => void;
}) {
  const { data } = usePlacementKundenfreigabe(placementId);
  const zustellen = useZustellenKundenfreigabe(placementId);
  const kanal = useKanalKundenfreigabe(placementId);
  const erneut = useErneutSendenKundenfreigabe(placementId);
  const versandAufloesen = useVersandAufloesenKundenfreigabe(placementId);
  const entscheid = useEntscheidKundenfreigabe(placementId);

  const [zustellenOpen, setZustellenOpen] = useState(false);
  const [kanalOpen, setKanalOpen] = useState(false);
  const [entscheidOpen, setEntscheidOpen] = useState(false);

  useEffect(() => {
    if (openKind === 'zustellen') setZustellenOpen(true);
    if (openKind === 'entscheid') setEntscheidOpen(true);
    if (openKind) onOpenHandled?.();
  }, [openKind, onOpenHandled]);

  if (!data) return null;
  const z = data.zustellung;

  const runZustellen = (body: KundenfreigabeZustellenBody): void => {
    zustellen.mutate(body, { onSuccess: () => setZustellenOpen(false) });
  };
  const runKanal = (body: KundenfreigabeKanalBody): void => {
    kanal.mutate(body, { onSuccess: () => setKanalOpen(false) });
  };
  const runEntscheid = (body: KundenentscheidErfassenBody): void => {
    entscheid.mutate(body, { onSuccess: () => setEntscheidOpen(false) });
  };

  return (
    <section className="rounded-lg border border-border p-4" data-testid="placement-kundenfreigabe-section">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">Kundenfreigabe & Entscheid</h3>
          <KundenfreigabeStatusBadge status={z?.status ?? null} />
        </div>
        <div className="flex gap-2">
          {canWrite && data.zustellen_erlaubt && (
            <Button size="sm" onClick={() => setZustellenOpen(true)} data-testid="kf-action-zustellen">Dem Kunden zustellen</Button>
          )}
          {canWrite && data.kanalwechsel_erlaubt && (
            <Button size="sm" variant="outline" onClick={() => setKanalOpen(true)} data-testid="kf-action-kanal">Kanal wechseln</Button>
          )}
          {canWrite && data.erneut_senden_erlaubt && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                if (window.confirm('Das Vergleichsdokument erneut ueber den aktuellen Kanal zustellen?')) erneut.mutate();
              }}
              data-testid="kf-action-erneut"
            >
              Erneut senden
            </Button>
          )}
          {canWrite && data.entscheid_erfassen_erlaubt && (
            <Button size="sm" variant="outline" onClick={() => setEntscheidOpen(true)} data-testid="kf-action-entscheid">Kundenentscheid erfassen</Button>
          )}
        </div>
      </div>

      {!data.zustellen_erlaubt && data.sperrgrund && !z && (
        <div className="mt-2 text-sm text-muted-foreground" data-testid="kf-sperrgrund">
          {data.sperrgrund === 'recommendation_not_released' && (
            <>
              {KUNDENFREIGABE_NICHT_FREIGEGEBEN_HINWEIS}{' '}
              <button className="underline" data-testid="kf-zu-schritt-4b">Zu Schritt 4b</button>
            </>
          )}
          {data.sperrgrund === 'report_veraltet' && KUNDENFREIGABE_VERALTET_HINWEIS}
          {data.sperrgrund === 'placement_status_invalid' && 'Kundenfreigabe erst nach der Empfehlung moeglich'}
          {data.sperrgrund === 'placement_locked' && 'Ausschreibung abgeschlossen - nur lesend'}
        </div>
      )}

      {z && (
        <div className="mt-3 rounded-md border border-border p-3 text-sm" data-testid="kf-zustellung-card">
          <div className="flex items-center gap-2">
            <Badge variant="neutral">{z.channel_label}</Badge>
            <span className="text-xs text-muted-foreground">{z.channel_quelle === 'broker' ? 'vom Broker gewaehlt' : 'gemaess Kanalsteuerung'}</span>
          </div>
          <p className="mt-1">
            zugestellt am {dateTimeShortDe(z.sent_at)} von {z.sent_by_name ?? '-'} · {z.versand_count}x gesendet
          </p>
          {z.kanalwechsel_count > 0 && z.channel_changed_at && (
            <p className="text-xs text-muted-foreground">Kanal gewechselt am {dateTimeShortDe(z.channel_changed_at)}</p>
          )}
          {z.versand_laeuft && <p className="text-xs text-amber-700">Versand laeuft …</p>}
          {z.versand_gesperrt && (
            <div className="space-y-1" data-testid="kf-versand-gesperrt">
              <p className="text-xs text-red-600">E-Mail gesendet, aber nicht erfasst. Bitte auflösen.</p>
              <div className="flex gap-2">
                {z.nachtragen_moeglich && (
                  <Button size="sm" variant="outline" disabled={!canWrite || versandAufloesen.isPending} onClick={() => versandAufloesen.mutate('nachtragen')} data-testid="kf-nachtragen">
                    Als versandt erfassen
                  </Button>
                )}
                <Button size="sm" variant="outline" disabled={!canWrite || versandAufloesen.isPending} onClick={() => versandAufloesen.mutate('freigeben')} data-testid="kf-freigeben">
                  Erneuten Versand freigeben
                </Button>
              </div>
            </div>
          )}
          {z.letzter_versandfehler && !z.versand_gesperrt && <p className="text-xs text-red-600">Versand fehlgeschlagen: {z.letzter_versandfehler}</p>}
          <p className="mt-1">Empfehlung: {z.empfehlung_carrier_name} ({z.empfehlung_submission_number})</p>
          <a className="text-primary underline" href={z.docx.datei_url} data-testid="kf-docx-download">{z.docx.filename}</a>
          {z.log.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs text-muted-foreground" data-testid="kf-log">
              {z.log.map((l) => (
                <li key={l.id}>
                  {l.art_label} · {l.channel === 'portal' ? 'Kundenportal' : 'E-Mail'}
                  {l.empfaenger ? ` an ${l.empfaenger}` : ''}
                  {l.grund ? ` · ${l.grund}` : ''} · {l.created_by_name ?? '-'} · {dateTimeShortDe(l.created_at)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {data.entscheide.map((d) => (
        <div key={d.id} className="mt-3 rounded-md border border-border p-3 text-sm" data-testid="kf-entscheid-card">
          <div className="flex items-center gap-2">
            <Badge variant={d.art === 'angenommen' ? 'success' : d.art === 'abgelehnt' ? 'danger' : 'info'}>{d.art_label}</Badge>
            <span className="text-xs text-muted-foreground">
              {dateTimeShortDe(d.decided_at)} - {d.entscheider_name ?? '-'} via {d.response_channel_label}
            </span>
          </div>
          {d.art === 'angenommen' && (
            <>
              <p className="mt-1">
                {d.gewaehlte_carrier_name} ({d.gewaehlte_submission_number}, V{d.gewaehlte_version_no}) - CHF {d.praemie_jahr ?? '-'} - Beginn{' '}
                {d.versicherungsbeginn ? dateShortDe(d.versicherungsbeginn) : '-'}
              </p>
              {d.abweichend && (
                <p className="text-xs text-amber-700">
                  <Badge variant="warning">{KUNDENENTSCHEID_ABWEICHEND_LABEL}</Badge> {d.abweichung_grund}
                </p>
              )}
              {d.report_veraltet_bei_entscheid && <Badge variant="warning">Entscheid auf veraltetem Vergleich</Badge>}
              {d.promotion && (
                <p className="mt-1 text-xs text-muted-foreground" data-testid="kf-promotion-line">
                  Promotion: {d.promotion.objekte} Objekt(e), {d.promotion.regeln} Soll-Vorgaben auf Police{' '}
                  <a className="text-primary underline" href={`/policen/${d.promotion.policy_id}?tab=risiko_deckung`} data-testid="kf-promotion-policy-link">
                    {d.promotion.policy_number ?? 'Entwurf'}
                  </a>{' '}
                  ({d.promotion.policy_neu ? 'neu angelegt' : 'bestehend'})
                </p>
              )}
            </>
          )}
          {(d.art === 'abgelehnt' || d.art === 'rueckfrage') && d.text && <p className="mt-1">{d.text}</p>}
        </div>
      ))}

      {data.freigabe && (
        <KundenfreigabeZustellenDialog
          open={zustellenOpen}
          onClose={() => setZustellenOpen(false)}
          facts={data}
          onZustellen={runZustellen}
          saving={zustellen.isPending}
          error={zustellen.error as { code?: string } | null}
        />
      )}
      {z && (
        <KundenfreigabeKanalDialog
          open={kanalOpen}
          onClose={() => setKanalOpen(false)}
          current={z.channel}
          facts={data}
          onWechseln={runKanal}
          saving={kanal.isPending}
          error={kanal.error as { code?: string } | null}
        />
      )}
      <KundenentscheidDialog
        open={entscheidOpen}
        onClose={() => setEntscheidOpen(false)}
        facts={data}
        onErfassen={runEntscheid}
        saving={entscheid.isPending}
        error={entscheid.error as { code?: string } | null}
      />
    </section>
  );
}
